import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import { ConflictNotice } from '../ui/ConflictNotice';
import { mergePreview, packlistKey, type LineView, type PacklistView } from './types';
import { DialogLayer } from '../ui/Dialog';

/**
 * "Scal powtórzenia (N)" (in the packlist's "…", owner 2026-10-21 23:39) opens this dialog: a preview of what merges
 * ("Kabel XLR 10 m: 8 + 16 → 24"), then one request with the lines as shown (a change in the meantime gives 409,
 * nothing merged).
 */
export function MergeDialog({ eventId, lines, groups, onClose }: { eventId: string; lines: LineView[]; groups: LineView[][]; onClose: () => void }) {
  const queryClient = useQueryClient();
  const merge = useMutation({
    mutationFn: () =>
      api<PacklistView>(`/api/packlists/${eventId}/merge-duplicates`, { method: 'POST', body: { lines: lines.map((l) => ({ id: l.id, version: l.version })) } }),
    onSuccess: (view) => {
      queryClient.setQueryData(packlistKey(eventId), view);
      onClose();
    },
  });
  const error = merge.error instanceof ApiError ? merge.error : null;
  const close = () => {
    merge.reset();
    onClose();
  };
  return (
        <div className="dialog-backdrop" data-feature-id="packlists.merge">
          <div className="dialog panel" role="dialog" aria-modal="true" aria-labelledby="merge-title">
            <DialogLayer onClose={close} busy={merge.isPending} />
            <h2 id="merge-title">Scalić powtórzenia?</h2>
            <p>Pozycje tego samego sprzętu z tym samym terminem staną się jedną: ilości, załadunek i zwrot się zsumują, uwagi połączą.</p>
            <ul className="merge-preview" aria-label="Co się połączy">
              {groups.map((g) => (
                <li key={g[0]!.id}>{mergePreview(g)}</li>
              ))}
            </ul>
            {error?.status === 409 && (
              <ConflictNotice
                onReload={() => {
                  close();
                  void queryClient.invalidateQueries({ queryKey: packlistKey(eventId) });
                }}
              />
            )}
            {error && error.status !== 409 && (
              <p className="alert" role="alert">
                {error.message}
              </p>
            )}
            <div className="actions">
              <button className="btn primary" type="button" autoFocus disabled={merge.isPending} onClick={() => merge.mutate()}>
                {merge.isPending ? 'Scalanie…' : 'Scal'}
              </button>
              <button className="btn" type="button" onClick={close}>
                Anuluj
              </button>
            </div>
          </div>
        </div>
  );
}
